import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// Navbar.jsx
// - Auto-closes the Bootstrap mobile collapse when a Link is clicked or when the route changes.
// - Uses Bootstrap's Collapse API if it's available on window.bootstrap, otherwise falls back to toggling the "show" class.
// - Only attempts to close on small screens (width < 992) so desktop behavior is unchanged.

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const closeNavbar = () => {
    if (typeof window === "undefined") return;

    // only close on mobile / collapsed breakpoint
    if (window.innerWidth >= 992) return;

    const navEl = document.getElementById("navbarNavDropdown");
    if (!navEl) return;

    // If Bootstrap JS is loaded on the page (bootstrap.bundle), use its Collapse API for proper animation/state
    // (Bootstrap 5 exposes window.bootstrap when the bundle is included globally)
    const bs = window && window.bootstrap ? window.bootstrap : null;
    if (bs && bs.Collapse) {
      const instance = bs.Collapse.getInstance(navEl) || new bs.Collapse(navEl, { toggle: false });
      instance.hide();
      return;
    }

    // Fallback: remove the 'show' class and fix aria-expanded on the toggler
    if (navEl.classList.contains("show")) {
      navEl.classList.remove("show");
      const toggler = document.querySelector(".navbar-toggler");
      if (toggler) toggler.setAttribute("aria-expanded", "false");
    }
  };

  // Close the navbar whenever the location changes
  useEffect(() => {
    closeNavbar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Escape key keyboard accessibility for closing modal dialog
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && showModal) {
        setShowModal(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showModal]);

  return (
    <>
      <style>{`
        /* NAVBAR BASE - Black Theme */
        .app-navbar {
          min-height: 64px;
          background: #000000;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
          border-bottom: 1px solid rgba(255,255,255,0.1);
          padding: 8px 24px;
        }

        .app-navbar .navbar-brand {
          display: flex;
          align-items: center;
          padding: 0;
          margin-right: 2rem;
          min-height: 44px;
        }

        .app-navbar .navbar-brand img {
          height: 38px;
          width: auto;
          max-height: 38px;
          object-fit: contain;
          border-radius: 4px;
        }

        /* TOGGLER - Custom White Outline */
        .navbar-toggler {
          border: 1px solid rgba(255,255,255,0.2) !important;
          min-height: 44px;
          min-width: 44px;
        }

        /* MOBILE DROPDOWN STYLING */
        @media (max-width: 991.98px) {
          .navbar-collapse {
            background: #000000; /* Solid Black Background */
            position: absolute;
            top: 64px;
            left: 0;
            right: 0;
            padding: 1rem 1.5rem 2rem 1.5rem;
            border-bottom: 2px solid #333;
            z-index: 1000;
          }
          
          .nav-item {
            border-bottom: 1px solid rgba(255,255,255,0.05);
            padding: 6px 0;
          }
          
          .admin-mobile-container {
            margin-top: 1.5rem;
          }
        }

        /* LINKS - White on Black */
        .app-navbar .nav-link {
          color: rgba(255,255,255,0.92) !important;
          font-weight: 600;
          font-size: 1rem;
          padding: 10px 14px !important;
          min-height: 44px;
          display: inline-flex;
          align-items: center;
          transition: all 0.2s ease;
        }

        .app-navbar .nav-link:hover {
          color: #ffffff !important;
          background: rgba(255,255,255,0.1);
          border-radius: 6px;
        }

        /* LOGIN/ADMIN BUTTON */
        .admin-btn {
          border: 1px solid rgba(255,255,255,0.4);
          color: #ffffff !important;
          border-radius: 6px;
          padding: 10px 24px;
          text-align: center;
          transition: 0.3s;
          background: transparent;
          cursor: pointer;
          min-height: 44px;
          font-weight: 700;
          font-size: 1rem;
        }

        .admin-btn:hover {
          background: rgba(255,255,255,0.15);
          border-color: #ffffff;
        }

        /* MODAL DIALOG POPUP */
        .login-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          animation: fadeIn 0.2s ease-out;
        }

        .login-modal-content {
          background: #121212;
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 16px;
          padding: 36px;
          width: 90%;
          max-width: 420px;
          position: relative;
          text-align: center;
          color: #ffffff;
          box-shadow: 0 20px 40px rgba(0,0,0,0.5);
          animation: scaleIn 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .login-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.7);
          font-size: 28px;
          cursor: pointer;
          transition: color 0.2s;
          line-height: 1;
          min-height: 44px;
          min-width: 44px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .login-modal-close:hover {
          color: #ffffff;
        }

        .login-modal-title {
          font-size: 24px;
          font-weight: 800;
          margin-bottom: 24px;
          color: #ffffff;
        }

        .login-modal-buttons {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .login-modal-btn {
          padding: 12px 18px;
          font-size: 1rem;
          font-weight: 700;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          min-height: 44px;
        }

        .login-modal-btn.admin {
          background: #ffffff;
          color: #000000;
        }

        .login-modal-btn.admin:hover {
          background: rgba(255, 255, 255, 0.9);
          transform: translateY(-2px);
        }

        .login-modal-btn.instructor {
          background: #007bff;
          color: #ffffff;
        }

        .login-modal-btn.instructor:hover {
          background: #0056b3;
          transform: translateY(-2px);
        }

        .login-modal-btn.student {
          background: transparent;
          color: #ffffff;
          border: 1.5px solid rgba(255, 255, 255, 0.4);
        }

        .login-modal-btn.student:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: #ffffff;
          transform: translateY(-2px);
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from { transform: scale(0.92); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>

      <nav className="navbar navbar-expand-lg navbar-dark app-navbar sticky-top" role="navigation" aria-label="Main Navigation">
        <div className="container-fluid">
          {/* Brand */}
          <Link className="navbar-brand" to="/" onClick={closeNavbar} aria-label="BrainOven Home">
              <img src="/brainoven-logo.jpg" alt="BrainOven Logo" height="38" />
          </Link>

          {/* Toggler */}
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNavDropdown"
            aria-controls="navbarNavDropdown"
            aria-expanded="false"
            aria-label="Toggle main navigation menu"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Collapsible Content */}
          <div className="collapse navbar-collapse" id="navbarNavDropdown">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0 ms-lg-4">
              <li className="nav-item">
                <Link className="nav-link" to="/" onClick={closeNavbar}>
                  Home
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/services" onClick={closeNavbar}>
                  Services
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/gallery" onClick={closeNavbar}>
                  Gallery
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/contactus" onClick={closeNavbar}>
                  Contact Us
                </Link>
              </li>
            </ul>

            {/* Login Button */}
            <div className="admin-mobile-container">
              <button 
                className="btn admin-btn d-block d-lg-inline-block w-100" 
                onClick={() => {
                  closeNavbar();
                  setShowModal(true);
                }}
                aria-label="Open portal login selector"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Modal Dialog with Accessibility */}
      {showModal && (
        <div 
          className="login-modal-overlay" 
          onClick={() => setShowModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="loginModalTitle"
        >
          <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="login-modal-close" 
              onClick={() => setShowModal(false)}
              aria-label="Close dialog"
            >
              &times;
            </button>
            <h3 id="loginModalTitle" className="login-modal-title">Log In As</h3>
            <div className="login-modal-buttons">
              <button 
                className="login-modal-btn admin" 
                onClick={() => {
                  setShowModal(false);
                  navigate("/admin");
                }}
                aria-label="Log in as Admin"
              >
                As Admin
              </button>
              <button 
                className="login-modal-btn instructor" 
                onClick={() => {
                  setShowModal(false);
                  navigate("/instructor/login");
                }}
                aria-label="Log in as Instructor"
              >
                As Instructor
              </button>
              <button 
                className="login-modal-btn student" 
                onClick={() => {
                  setShowModal(false);
                  navigate("/student/login");
                }}
                aria-label="Log in as Student"
              >
                As Student
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
